import React, { createContext, useEffect, useReducer, useRef, useState } from 'react';
import SplashScreen from 'src/components/SplashScreen';
import { PublicClientApplication } from '@azure/msal-browser';
import {
  getActiveOrCachedAccount,
  getUserInfo,
  initializeMsalInstance,
  isInteractionRequiredError,
  isSilentIframeTimeoutError,
} from 'src/utilities/msalUtilities';
import { msalConfig } from 'src/config';
import { getProcessAiGroupName, CONSOLE_DEVELOPERS_GROUP, CONSOLE_ADMINS_GROUP } from 'src/constants';
import { clearAllCaches } from 'src/utilities/AppCaches';
import { enqueueSnackbar } from 'notistack';

const instance = (() => {
  if (typeof window !== 'undefined' && !window.__msalInstance) {
    window.__msalInstance = new PublicClientApplication(msalConfig);
  }
  return window.__msalInstance || new PublicClientApplication(msalConfig);
})();

const CYPRESS_AUTH_USER_STORAGE_KEY = 'lantern:cypress:authUser';
const LOCAL_E2E_AUTH_BYPASS_KEY = 'lantern:e2eAuthBypass';
const E2E_AUTH_BYPASS_ENABLED = process.env.REACT_APP_ENABLE_E2E_AUTH_BYPASS === 'true';
const LOGIN_SCOPES = ['openid', 'profile', 'User.Read'];
const INTERACTION_IN_PROGRESS_ERROR = 'interaction_in_progress';
const CACHED_AUTH_USER_STORAGE_KEY = 'lantern:auth:user';
const AUTH_RECOVERY_EVENT = 'lantern:auth-expired';

const getCypressAuthUser = () => {
  if (typeof window === 'undefined') return null;
  const hostname = window.location?.hostname || '';
  const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
  const localBypassEnabled = localStorage.getItem(LOCAL_E2E_AUTH_BYPASS_KEY) === 'true';
  if ((!E2E_AUTH_BYPASS_ENABLED && !localBypassEnabled) || !isLocalHost) return null;
  const directUser = window.__LANTERNAL_CYPRESS_AUTH_USER;
  if (directUser && typeof directUser === 'object') {
    const groups = Array.isArray(directUser.groups) ? directUser.groups : [];
    return {
      id: directUser.id || 'cypress-user',
      displayName: directUser.displayName || 'Cypress User',
      mail: directUser.mail || 'cypress.user@localhost',
      groups,
      ...directUser,
    };
  }

  const raw = localStorage.getItem(CYPRESS_AUTH_USER_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const groups = Array.isArray(parsed.groups) ? parsed.groups : [];
    return {
      id: parsed.id || 'cypress-user',
      displayName: parsed.displayName || 'Cypress User',
      mail: parsed.mail || 'cypress.user@localhost',
      groups,
      ...parsed,
    };
  } catch (_error) {
    return null;
  }
};

const readCachedAuthUser = () => {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(CACHED_AUTH_USER_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (_error) {
    return null;
  }
};

const persistCachedAuthUser = (user) => {
  if (typeof window === 'undefined') return;
  if (!user || typeof user !== 'object') return;
  try {
    localStorage.setItem(CACHED_AUTH_USER_STORAGE_KEY, JSON.stringify(user));
  } catch (_error) {
    // best effort only
  }
};

const initialAuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  hasGroup: true,
  hasPermission: () => false,
  hasProcessAIPermission: () => false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INITIALISE': {
      const { isAuthenticated, user } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialized: true,
        user,
      };
    }
    case 'LOGIN': {
      const { user } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        user,
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        isInitialized: true,
        user: null,
      };
    }
    default: {
      return state;
    }
  }
};

const AuthContext = createContext({
  ...initialAuthState,
  loginWithRedirect: () => Promise.resolve(),
  logout: () => Promise.resolve(),
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState, (baseState) => {
    const bootstrappedUser = getCypressAuthUser();
    if (!bootstrappedUser) return baseState;
    return {
      ...baseState,
      isAuthenticated: true,
      user: bootstrappedUser,
    };
  });
  const isMountedRef = useRef(true);
  const loginInFlightRef = useRef(null);
  const hasShownInteractiveReauthNoticeRef = useRef(false);
  const [authStatusMessage, setAuthStatusMessage] = useState('');
  const [authActionLabel, setAuthActionLabel] = useState('');

  const safeDispatch = (action) => {
    if (isMountedRef.current) {
      dispatch(action);
    }
  };

  const isInteractionInProgressError = (error) => {
    const errorCode = error?.errorCode || error?.code || '';
    const message = error?.message || '';
    return errorCode === INTERACTION_IN_PROGRESS_ERROR || message.includes(INTERACTION_IN_PROGRESS_ERROR);
  };

  const forceInteractiveLogin = async (reason = 'timeout') => {
    const inIframe = window.self !== window.top;
    const isTimeout = reason === 'timeout';
    const isManualRetry = reason === 'manualRetry';
    const reauthMessage = isTimeout
      ? inIframe
        ? 'Session refresh timed out. Please complete sign-in in the popup to continue.'
        : 'Session refresh timed out. Redirecting to Microsoft sign-in.'
      : inIframe
        ? 'Session could not be restored. Please complete sign-in in the popup to continue.'
        : 'Session could not be restored. Redirecting to Microsoft sign-in.';

    setAuthStatusMessage(reauthMessage);
    setAuthActionLabel('');

    if (!hasShownInteractiveReauthNoticeRef.current) {
      hasShownInteractiveReauthNoticeRef.current = true;
      enqueueSnackbar('Session refresh timed out. Reconnecting your sign-in.', {
        autoHideDuration: 4500,
        variant: 'info',
      });
    }

    if (inIframe) {
      if (!isManualRetry) {
        setAuthStatusMessage('Your Microsoft session needs to be refreshed.');
        setAuthActionLabel('Sign in again');
        return;
      }

      const popupResult = await instance.loginPopup({ scopes: LOGIN_SCOPES });
      if (popupResult?.account) {
        instance.setActiveAccount(popupResult.account);
      }
      setAuthStatusMessage('');
      setAuthActionLabel('');
      return;
    }

    await instance.loginRedirect({ scopes: LOGIN_SCOPES });
  };

  useEffect(() => {
    isMountedRef.current = true;

    const initializeAuth = async () => {
      if (state.user) {
        safeDispatch({
          type: 'INITIALISE',
          payload: {
            isAuthenticated: true,
            user: state.user,
          },
        });
        return;
      }

      const cypressAuthUser = getCypressAuthUser();
      if (cypressAuthUser) {
        safeDispatch({
          type: 'INITIALISE',
          payload: {
            isAuthenticated: true,
            user: cypressAuthUser,
          },
        });
        return;
      }

      try {
        const telemetryKeys = Object.keys(localStorage).filter((storageKey) => {
          const storageValue = localStorage.getItem(storageKey);
          return typeof storageValue === 'string' && storageValue.startsWith('{"failedRequests');
        });

        telemetryKeys.forEach((key) => {
          const telemetryString = localStorage.getItem(key);
          const serverTelemetry = telemetryString ? JSON.parse(telemetryString) : null;
          if (Array.isArray(serverTelemetry?.failedRequests) && serverTelemetry.failedRequests.length > 0) {
            // Only remove noisy MSAL telemetry entries. Clearing all localStorage drops valid auth/session cache.
            localStorage.removeItem(key);
          }
        });
      } catch (error) {
        console.warn('Failed to process MSAL telemetry cache cleanup:', error);
      }

      await handleLogin();
    };

    initializeAuth();

    return () => {
      isMountedRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async () => {
    if (loginInFlightRef.current) {
      return loginInFlightRef.current;
    }

    loginInFlightRef.current = (async () => {
      safeDispatch({
        type: 'INITIALISE',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });

      const inIframe = window.self !== window.top;

      const dispatchLoginUser = async (accountFallback = null) => {
        try {
          const userInfo = await getUserInfo();
          hasShownInteractiveReauthNoticeRef.current = false;
          setAuthStatusMessage('');
          setAuthActionLabel('');
          persistCachedAuthUser(userInfo);
          safeDispatch({
            type: 'LOGIN',
            payload: {
              user: userInfo,
            },
          });
          return true;
        } catch (error) {
          if (isInteractionRequiredError(error)) {
            throw error;
          }

          if (isSilentIframeTimeoutError(error)) {
            const cachedUser = readCachedAuthUser();
            const fallbackUser = cachedUser || accountFallback;
            const hasCachedAuthToken = Boolean(localStorage.getItem('auth'));
            if (fallbackUser) {
              if (hasCachedAuthToken) {
                console.warn('Silent token refresh timed out; continuing with cached auth user.', error);
                setAuthStatusMessage('');
                safeDispatch({
                  type: 'LOGIN',
                  payload: {
                    user: fallbackUser,
                  },
                });
                return true;
              }

              try {
                await forceInteractiveLogin('timeout');
                return false;
              } catch (interactiveError) {
                if (isInteractionInProgressError(interactiveError)) {
                  return false;
                }
                console.error('Interactive re-authentication failed after silent timeout:', interactiveError);
                throw interactiveError;
              }
            }
          }

          if (error?.message?.includes('No account available for token acquisition')) {
            const cachedUser = readCachedAuthUser();
            if (cachedUser) {
              console.warn('No active account available; continuing with cached auth user.');
              setAuthStatusMessage('');
              safeDispatch({
                type: 'LOGIN',
                payload: {
                  user: cachedUser,
                },
              });
              return true;
            }

            try {
              await forceInteractiveLogin('missingAccount');
              return false;
            } catch (interactiveError) {
              if (isInteractionInProgressError(interactiveError)) {
                return false;
              }

              console.error('Interactive re-authentication failed:', interactiveError);
              throw interactiveError;
            }
          }

          if (accountFallback) {
            console.warn('Failed to get user info, using account data:', error);
            persistCachedAuthUser(accountFallback);
            safeDispatch({
              type: 'LOGIN',
              payload: {
                user: accountFallback,
              },
            });
            return true;
          }

          throw error;
        }
      };

      try {
        await initializeMsalInstance();

        let activeAccount = null;

        if (!inIframe) {
          const response = await instance.handleRedirectPromise();
          if (response?.account) {
            activeAccount = response.account;
            instance.setActiveAccount(activeAccount);
          }
        }

        if (!activeAccount) {
          activeAccount = getActiveOrCachedAccount();
          if (activeAccount) {
            instance.setActiveAccount(activeAccount);
          }
        }

        if (!activeAccount) {
          const cachedUser = readCachedAuthUser();
          const hasCachedAuthToken = Boolean(localStorage.getItem('auth'));
          if (cachedUser && hasCachedAuthToken) {
            console.warn('No active MSAL account found on startup; using cached auth user.');
            setAuthStatusMessage('');
            safeDispatch({
              type: 'LOGIN',
              payload: {
                user: cachedUser,
              },
            });
            return;
          }

          if (inIframe) {
            try {
              const silentResult = await instance.ssoSilent({ scopes: LOGIN_SCOPES });
              activeAccount = silentResult?.account || null;
            } catch {
              await forceInteractiveLogin('missingAccount');
              return;
            }
          } else {
            await forceInteractiveLogin('missingAccount');
            return;
          }
        }

        if (activeAccount) {
          instance.setActiveAccount(activeAccount);
        }

        await dispatchLoginUser(activeAccount);
      } catch (error) {
        if (isInteractionInProgressError(error)) {
          return;
        }

        console.error('Failed to initialize MSAL or handle login:', error);
        setAuthStatusMessage('Unable to complete sign-in. Please retry.');
        setAuthActionLabel('Sign in again');
        safeDispatch({
          type: 'INITIALISE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    })();

    try {
      await loginInFlightRef.current;
    } finally {
      loginInFlightRef.current = null;
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleAuthRecoveryEvent = async () => {
      if (loginInFlightRef.current) return;

      try {
        await handleLogin();
      } catch (error) {
        if (isInteractionInProgressError(error)) {
          return;
        }
        console.warn('Failed to recover authentication from API response event:', error);
      }
    };

    window.addEventListener(AUTH_RECOVERY_EVENT, handleAuthRecoveryEvent);
    return () => {
      window.removeEventListener(AUTH_RECOVERY_EVENT, handleAuthRecoveryEvent);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    await initializeMsalInstance();

    await instance.handleRedirectPromise();

    // Clear all application caches for security
    try {
      clearAllCaches();
    } catch (error) {
      console.warn('Failed to clear caches during logout:', error);
    }
    // Clear auth token from localStorage
    localStorage.removeItem('auth');
    localStorage.removeItem(CACHED_AUTH_USER_STORAGE_KEY);
    setAuthStatusMessage('');
    setAuthActionLabel('');
    hasShownInteractiveReauthNoticeRef.current = false;

    await instance.logoutRedirect({
      postLogoutRedirectUri: '/',
    });
    safeDispatch({
      type: 'LOGOUT',
      payload: {
        isAuthenticated: false,
        user: null,
      },
    });
  };

  const handleManualReauth = async () => {
    try {
      await forceInteractiveLogin('manualRetry');
      if (!loginInFlightRef.current) {
        await handleLogin();
      }
    } catch (error) {
      if (isInteractionInProgressError(error)) {
        return;
      }

      console.error('Manual re-authentication failed:', error);
      setAuthStatusMessage('Unable to complete sign-in. Please retry.');
      setAuthActionLabel('Sign in again');
    }
  };

  const hasPermission = (role) => {
    if (!Array.isArray(state?.user?.groups)) {
      return false;
    }

    const isDev = state.user.groups.indexOf(CONSOLE_DEVELOPERS_GROUP) !== -1;
    const isAdmin = state.user.groups.indexOf(CONSOLE_ADMINS_GROUP) !== -1;

    if (isDev) return true;
    if (isAdmin) return role !== CONSOLE_DEVELOPERS_GROUP;

    return state.user.groups.indexOf(role) !== -1;
  };

  const hasProcessAIPermission = () => {
    if (!Array.isArray(state?.user?.groups)) {
      return false;
    }

    return state.user.groups.indexOf(getProcessAiGroupName()) !== -1;
  };

  if (!state.user) {
    return (
      <SplashScreen
        message={authStatusMessage}
        actionLabel={authActionLabel}
        onAction={authActionLabel ? handleManualReauth : null}
      />
    );
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        loginWithRedirect: () => handleLogin(),
        logout: () => handleLogout(),
        hasPermission,
        hasProcessAIPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
